<template>
  <div class="tpg-chat" data-testid="tpg-chat-panel">
    <!-- L9 — another tab already switched version and the backend abandoned the form. -->
    <v-alert
      v-if="versionAbandonedFormId"
      type="warning"
      variant="tonal"
      density="compact"
      class="mb-2"
      data-testid="tpg-version-abandoned-alert"
    >ฟอร์ม #{{ versionAbandonedFormId }} ถูกทิ้งเพราะเวอร์ชันเปลี่ยน — คำถามเริ่มใหม่จากข้อแรก</v-alert>

    <!-- 🔴 Only offered when a card actually parsed — an always-on switch would read as a
         page-wide mode and invite "why is nothing different?" on ordinary turns. -->
    <div v-if="hasCards" class="d-flex justify-end">
      <v-switch
        v-model="rawWire"
        density="compact"
        hide-details
        color="primary"
        label="ดู marker ดิบ"
        class="tpg-raw-switch flex-grow-0"
        data-testid="tpg-raw-wire-toggle"
      />
    </div>

    <!-- ── bubbles ───────────────────────────────────────────────────────── -->
    <div class="chat-scroll" data-testid="tpg-chat-bubbles">
      <div
        v-if="!bubbles.length && !sending"
        class="text-medium-emphasis text-caption pa-2"
        data-testid="tpg-chat-empty"
      >ยังไม่มีบทสนทนา — พิมพ์ข้อความเพื่อเริ่มทดสอบ (พิมพ์อะไรก็ได้ ระบบจะเริ่มถามข้อแรกให้)</div>

      <div
        v-for="b in bubbles"
        :key="b.key"
        class="d-flex mb-2"
        :class="b.side === 'user' ? 'justify-end' : 'justify-start'"
      >
        <div
          class="chat-col d-flex flex-column"
          :class="[b.side === 'user' ? 'align-end' : 'align-start', { 'chat-col--card': b.cards && b.cards.length }]"
        >
          <!-- 🔴 A CARD-ONLY turn (blank lead text + marker — a shape the node supports on purpose, since
               most cards carry their own title) would otherwise leave an empty grey bubble floating above
               the card. The bubble is dropped ONLY when there is nothing to say AND a card is there to say
               it instead; every card-less bubble — user, system, plain bot — keeps rendering as before,
               empty or not. Selecting such a turn still works from the timeline strip. -->
          <!-- T12 — the one thing a bare chat bubble cannot say about itself: this text never went through
               `/chat`, cost no turn and wrote nothing. Only the peek bubble carries `meta`. -->
          <div
            v-if="b.meta"
            class="text-caption text-medium-emphasis mb-1"
            :data-testid="'tpg-bubble-meta-' + (b.turnId || 0)"
          >{{ b.meta }}</div>
          <div
            v-if="bubbleText(b).trim() || !(b.cards && b.cards.length)"
            class="chat-bubble"
            :class="bubbleClass(b)"
            :title="b.turnId ? 'เทิร์น #' + b.turnId + ' — คลิกเพื่อดูรายละเอียดในคอลัมน์ขวา' : null"
            :data-testid="'tpg-bubble-' + (b.turnId || 0) + '-' + b.side"
            @click="onBubble(b)"
          >
            <span class="sr-only">{{ srLabel(b) }}</span>{{ bubbleText(b) }}
          </div>

          <!-- 🔴 Cards sit OUTSIDE `.chat-bubble` so expanding one cannot select the turn, and `.stop`
               keeps it that way if the markup is ever nested back in. -->
          <div
            v-if="b.cards && b.cards.length"
            class="tpg-cards mt-2"
            :data-testid="'tpg-bubble-cards-' + (b.turnId || 0)"
            @click.stop
          >
            <!-- 🔴 One slot testid PER CARD. A turn that skips two SUMMARY questions sends the marker twice
                 (TaxCardMarkerV2.cs), so two cards in one bubble is normal traffic — without the per-card
                 scope every testid inside the card (`tpg-card`, `tpg-card-cat-0`, …) repeats and can no
                 longer name which of the two it belongs to. -->
            <div
              v-for="(c, ci) in b.cards"
              :key="ci"
              class="mb-2"
              :data-testid="'tpg-card-slot-' + (b.turnId || 0) + '-' + ci"
            >
              <!-- A card whose JSON did not parse is still EVIDENCE — show the raw text, never drop it.
                   🔴 `card` is a required prop, so a null `doc` must never reach it. -->
              <v-alert
                v-if="!c.doc"
                type="error"
                variant="tonal"
                density="compact"
                :data-testid="'tpg-card-broken-' + (b.turnId || 0) + '-' + ci"
              >
                <div class="text-caption font-weight-medium">อ่าน payload ของการ์ดใบนี้ไม่ได้ (JSON ไม่ถูกต้อง)</div>
                <pre class="tpg-card-raw">{{ c.raw }}</pre>
              </v-alert>
              <!-- 🔴 Only the NEWEST bot bubble's card may be acted on. An older turn is
                   a record of what was on screen then, and posting a slider from it would
                   submit a plan against a question the form has already moved past —
                   which the backend refuses anyway (the postback names a stale fieldKey),
                   so an editable old card is a control that can only ever fail. -->
              <TaxSummaryCardV2
                v-else
                :card="c.doc"
                :raw="c.raw"
                :interactive="isLiveBubble(b)"
                :busy="sending"
                @card-submit="onCardSubmit"
              />
            </div>

            <v-chip
              v-if="b.cardsDiffer"
              size="x-small"
              color="orange"
              variant="tonal"
              prepend-icon="mdi-alert"
              title="ฝั่งหน้าเว็บกับ backend แยกการ์ดจากข้อความเดียวกันได้จำนวนไม่เท่ากัน — ตรวจรูปแบบ marker"
              :data-testid="'tpg-cards-differ-' + (b.turnId || 0)"
            >จำนวนการ์ดที่แยกได้ไม่ตรงกับที่ backend ส่งมา</v-chip>
          </div>

          <div class="d-flex align-center ga-1 mt-1">
            <span v-if="b.time" class="chat-time text-caption text-medium-emphasis">{{ fmtTime(b.time) }}</span>
            <!-- 🔴 L6 — orange, never red: the node is fail-soft and the turn itself succeeded. -->
            <v-chip
              v-if="b.llmFailed"
              size="x-small"
              color="orange"
              variant="tonal"
              prepend-icon="mdi-alert"
              title="มีการเรียก LLM ที่ล้มในเทิร์นนี้ — ดูรายละเอียดในแท็บ Prompt & LLM"
              :data-testid="'tpg-bubble-llmfail-' + (b.turnId || 0)"
            >LLM ล้ม (fail-soft)</v-chip>
          </div>
        </div>
      </div>

      <!-- L5 (ข) — the bot placeholder while the turn is in flight. NO cancel button. -->
      <div v-if="sending" class="d-flex justify-start mb-2">
        <div class="chat-col d-flex flex-column align-start">
          <div class="chat-bubble chat-bubble--bot chat-bubble--pending">
            <v-skeleton-loader type="text@2" data-testid="tpg-sending-skeleton" />
          </div>
          <span class="text-caption text-medium-emphasis mt-1" data-testid="tpg-sending-caption">
            กำลังประมวลผล… (เทิร์นนี้อาจเรียก LLM ได้ถึง 2 ครั้ง)<template v-if="elapsedSec > 3"> · ผ่านไป {{ elapsedSec }} วิ</template>
          </span>
          <span
            v-if="elapsedSec > 60"
            class="text-caption text-orange mt-1"
            data-testid="tpg-sending-slow"
          >ใช้เวลานานผิดปกติ — ตรวจโมเดล/คอนฟิก LLM</span>
        </div>
      </div>
    </div>

    <!-- ── quick reply (L11) ─────────────────────────────────────────────── -->
    <div v-if="visibleQuickReply.length" class="mt-2" data-testid="tpg-quickreply">
      <div class="d-flex flex-wrap ga-1">
        <v-chip
          v-for="(q, i) in visibleQuickReply"
          :key="i"
          link
          tabindex="0"
          size="small"
          variant="outlined"
          :disabled="!canSend"
          :title="q.label"
          :data-testid="'tpg-quickreply-chip-' + i"
          @click="onChip(q)"
          @keydown.enter.prevent="onChip(q)"
          @keydown.space.prevent="onChip(q)"
        >{{ q.label }}</v-chip>

        <v-btn
          v-if="quickReplyHasMore"
          size="x-small"
          variant="text"
          data-testid="tpg-quickreply-showall"
          @click="showAllQuick = true"
        >ดูทั้งหมด ({{ quickReply.length }})</v-btn>
      </div>
      <div class="text-caption text-medium-emphasis mt-1" data-testid="tpg-quickreply-source">
        ที่มา: {{ quickSourceTh }}
      </div>
    </div>
    <div
      v-else
      class="text-caption text-medium-emphasis mt-2"
      data-testid="tpg-quickreply-none"
    >— ไม่มี quick reply สำหรับคำถามนี้ ({{ quickSourceTh }}) —</div>

    <!-- ── L12 completed banner — sits right above the composer, on purpose ── -->
    <v-alert
      v-if="isCompleted"
      type="success"
      variant="tonal"
      density="comfortable"
      icon="mdi-check-circle-outline"
      :closable="false"
      class="mt-3"
      data-testid="tpg-completed-banner"
    >
      <div class="font-weight-medium">ฟอร์มนี้ตอบครบแล้ว</div>
      <div class="text-caption">ยังไม่มีผลคำนวณภาษีในเฟสนี้ — ถ้าพิมพ์ต่อ ระบบจะยังรับข้อความ แต่จะไม่มีคำถามใหม่</div>
      <template #append>
        <div class="d-flex flex-wrap ga-1">
          <v-btn
            size="small"
            variant="tonal"
            data-testid="tpg-completed-newform"
            @click="$emit('new-form')"
          >เริ่มฟอร์มใหม่</v-btn>
          <v-btn
            size="small"
            variant="text"
            data-testid="tpg-completed-viewjson"
            @click="$emit('view-tax-json')"
          >ดู Tax JSON</v-btn>
        </div>
      </template>
    </v-alert>

    <!-- ── composer (stays usable after Completed — that is the point) ────── -->
    <div class="tpg-composer mt-3">
      <div class="d-flex align-end ga-2">
        <v-textarea
          v-model="draft"
          :placeholder="placeholder"
          :disabled="sending"
          rows="2"
          auto-grow
          density="compact"
          variant="outlined"
          hide-details
          aria-label="ข้อความที่จะส่งเข้าโหมดทดสอบ"
          data-testid="tpg-chat-input"
          @keydown.enter.exact.prevent="submit"
        />
        <v-btn
          color="primary"
          variant="flat"
          rounded="lg"
          :loading="sending"
          :disabled="!canSend"
          data-testid="tpg-chat-send"
          @click="submit"
        >
          <v-icon start>mdi-send</v-icon>
          ส่ง
        </v-btn>
      </div>
      <div class="text-caption text-medium-emphasis mt-1" data-testid="tpg-pdpa-note">
        ห้ามพิมพ์ข้อมูลจริงของลูกค้า — ข้อความทุกเทิร์นถูกบันทึกไว้ตรวจสอบ
      </div>
    </div>
  </div>
</template>

<script>
// Chat column of the Tax Playground V2 console (S3-P4 · task 4-A).
//
// A dumb component: the parent owns every piece of state that survives a turn
// (bubbles are pre-assembled there, because the full `reply` of the newest turn
// lives on `activeTurn` while `/turns` only carries a 160-char `replyPreview`).
//
// 🔴 `quickReplySource` may be `null`, NOT `"none"`: `/state` on the `hasForm:false`
// path returns the DTO default while `/chat` sets `"none"` explicitly. Both must
// read as "คำถามนี้เป็นการพิมพ์ตอบ" — `QUICK_SOURCE_TH` therefore falls back to
// the `none` wording for null and for any unknown value.
//
// 🔴 The composer stays ENABLED after the form completes (L12). "Typing after the
// form is done" is a bug class that shipped in V1, so it has to be testable here.
//
// 🔴 Tax cards: the parent splits `|taxcard|` payloads out of the raw reply and hands
// them down ON the bubble (`cards` / `cardText` / `cardsDiffer`) — this component never
// parses the wire itself. Cards render BELOW the bubble, as an ADDITION: the bubble text
// still defaults to the marker-free copy, and the "ดู marker ดิบ" switch puts the raw
// string with markers back, because checking where the marker sits relative to the
// question after it is the reason this screen exists. `cardsDiffer` flags the FE split
// disagreeing with the backend's own — format drift, shown rather than smoothed over; the
// parent raises it only when every payload parsed, so it never doubles up on the broken-payload
// alert sitting in the same column.

import TaxSummaryCardV2 from './TaxSummaryCardV2.vue';
import { fmtThaiTime } from '../utils/thaiTime.js';

/**
 * The 6 quickReply sources (L11), plus the null/unknown fallback.
 * The status-screen ones (`startConfirm*` / `resume*` / `completion*` /
 * `versionChoice*`) ride along with the `START_CONFIRM` / `RESUME` / `COMPLETED`
 * kinds. Without them those chips would be labelled "คำถามนี้เป็นการพิมพ์ตอบ" —
 * precisely wrong, since the chips ARE the expected answer. Values are the
 * backend's `TaxFormV2BAL.ChipSource*` / `TaxQuickReplyV2.Source*` constants;
 * the kind label that pairs with them is `STATE_KIND_TH` in `TaxPlaygroundV2.vue`.
 */
const QUICK_SOURCE_TH = {
  answerOptions: 'จากตัวเลือกในคอนฟิก',
  confirm: 'ใช่/ไม่ใช่',
  incomeCategories: 'ประเภทเงินได้',
  yearSelect: 'เลือกปีภาษี',
  optedOut: 'ปิดไว้ที่คอนฟิกข้อนี้',
  clarify: 'ตัวเลือกของคำถามย้อนกลับ',
  clarifyLlm: 'ตัวเลือกของคำถามย้อนกลับ (LLM ปรุงข้อความ)',
  none: 'คำถามนี้เป็นการพิมพ์ตอบ',
  startConfirm: 'ปุ่มประจำข้อความทักทาย',
  resume: 'ปุ่มประจำข้อความทำต่อ',
  startConfirmMarker: 'ปุ่มที่ผู้เขียน prompt กำหนดเอง (ข้อความทักทาย)',
  resumeMarker: 'ปุ่มที่ผู้เขียน prompt กำหนดเอง (ข้อความทำต่อ)',
  // Finished-plan screens (kind COMPLETED / RESUME) — without these their chips read as "พิมพ์ตอบ" too.
  completion: 'ปุ่มระบบ: เริ่มวางแผนภาษีใหม่ (ข้อความปิดฟอร์ม)',
  completionMarker: 'ปุ่มที่ผู้เขียน prompt กำหนดเอง (ข้อความปิดฟอร์ม)',
  // Kind is plain RESUME on this screen — the source is the only thing telling it apart from «ทำต่อ».
  versionChoice: 'ปุ่มระบบ: สร้างแผนใหม่ / ใช้ข้อมูลเดิม (แผนอยู่บนเวอร์ชันเก่า)',
  versionChoiceMarker: 'ปุ่มจาก marker ในข้อความยืนยันเวอร์ชัน',
  // QuickReplyMode = off in Settings — a deliberate switch, not "this question is typed".
  off: 'ปิดปุ่มตอบด่วนทั้งระบบที่ Settings (off)',
};

/** Chips shown before the "ดูทั้งหมด" button kicks in on a narrow screen. */
const QUICK_COLLAPSED_MAX = 6;

export default {
  name: 'TaxPlaygroundChatPanelV2',
  components: { TaxSummaryCardV2 },
  props: {
    /**
     * `{ key, side: 'user'|'bot'|'system'|'state', text, time, turnId, llmFailed }`, plus:
     * `meta`     — a caption rendered ABOVE the bubble. Set ONLY by the `state` bubble (the "Get Tax State"
     *   peek), which is the one entry that never went through `/chat` and must say so.
     * …and on bot bubbles:
     * `cardText` — `text` with every `|taxcard|` payload removed, or `null` when the turn has no card.
     *   🔴 `''` is a THIRD case, not the same as `null`: the turn's whole message was the marker
     *   (a card-only turn). See `bubbleText`.
     * `cards`    — `{ raw, doc }[]`; `doc` is `null` when that payload failed to parse (`raw` still shown)
     * `cardsDiffer` — the FE split and the backend's `taxCards` array disagree on how many cards there
     *   are, and every payload parsed (a broken one is counted by the two sides differently by design)
     */
    bubbles: { type: Array, default: () => [] },
    /** `{ label, value }[]` */
    quickReply: { type: Array, default: () => [] },
    quickReplySource: { type: String, default: null },
    sending: { type: Boolean, default: false },
    elapsedSec: { type: Number, default: 0 },
    canSend: { type: Boolean, default: false },
    formStatus: { type: String, default: null },
    formId: { type: Number, default: 0 },
    versionAbandonedFormId: { type: Number, default: null },
  },
  emits: ['send', 'quick-reply', 'new-form', 'view-tax-json', 'select-turn', 'card-submit'],
  data() {
    return {
      draft: '',
      showAllQuick: false,
      /** Local-only: flips every bubble back to the raw reply, markers included. */
      rawWire: false,
    };
  },
  computed: {
    isCompleted() {
      return this.formStatus === 'Completed';
    },
    hasCards() {
      return (this.bubbles || []).some((b) => b && b.cards && b.cards.length);
    },
    /** The last bot bubble — the only one whose card is still the question on screen.
     * 🔴 `=== 'bot'` and not "the last bubble": the peek bubble (`side: 'state'`) is appended AFTER the
     * newest real reply, and if it counted here the live card's inputs and its บันทึก button would go
     * read-only with no error — the card postback path this console exists to exercise, silently dead. */
    liveBubbleKey() {
      const list = (this.bubbles || []).filter((b) => b && b.side === 'bot');
      const last = list[list.length - 1];
      return last ? last.key : null;
    },
    /** `null` and any unknown value read as `none` — see the file header. */
    quickSourceTh() {
      return QUICK_SOURCE_TH[this.quickReplySource] || QUICK_SOURCE_TH.none;
    },
    quickReplyHasMore() {
      const all = this.quickReply || [];
      return this.isXs && !this.showAllQuick && all.length > QUICK_COLLAPSED_MAX;
    },
    visibleQuickReply() {
      const all = this.quickReply || [];
      return this.quickReplyHasMore ? all.slice(0, QUICK_COLLAPSED_MAX) : all;
    },
    isXs() {
      return Boolean(this.$vuetify && this.$vuetify.display && this.$vuetify.display.xs);
    },
    placeholder() {
      return this.isCompleted ? 'พิมพ์เพื่อทดสอบพฤติกรรมหลังฟอร์มจบ…' : 'พิมพ์ข้อความ…';
    },
  },
  methods: {
    /** Enter sends; Shift+Enter falls through to the textarea's own newline. */
    submit() {
      if (!this.canSend) return;
      const text = String(this.draft || '').trim();
      if (!text) return;
      this.$emit('send', text);
      this.draft = '';
    },
    /**
     * 🔴 A chip TYPES what it shows — `label` is the whole chip, there is no hidden value behind it.
     * (A `{label,value}` pair is what made pressing "ชาย" put "male" in the chat bubble.)
     */
    onChip(q) {
      if (!this.canSend) return;
      const text = (q && q.label) || '';
      if (!text) return;
      this.$emit('quick-reply', text);
    },
    /**
     * 🔴 Falls back to `b.text` only when `cardText` is ABSENT (`null`/undefined) — user/system bubbles
     * and every card-less turn never had a stripped copy, and they must still show their own text.
     * `''` is NOT absent: it is a card-only turn whose whole message WAS the marker, and falling back
     * there would put the raw `|taxcard|(…)` payload on screen with the switch still off.
     * 🔴 Always a string — the template calls `.trim()` on the result to decide whether to render at all.
     */
    bubbleText(b) {
      if (!b) return '';
      const text = this.rawWire || b.cardText == null ? b.text : b.cardText;
      return text == null ? '' : String(text);
    },
    /**
     * Is this the bubble the customer is currently looking at — and can we still send?
     * 🔴 Both halves matter. `canSend` is false on a completed form and while a turn is
     * in flight, and a slider that submits in either state posts a plan against a form
     * that is no longer asking for one.
     */
    isLiveBubble(b) {
      return Boolean(this.canSend && b && b.key && b.key === this.liveBubbleKey);
    },
    /**
     * A card asked for something. Passed straight up with no interpretation: the view
     * owns the API call, and the FIELD KEY the postback needs is the question currently
     * on screen — which only the view knows.
     */
    onCardSubmit(payload) {
      if (!this.canSend) return;
      this.$emit('card-submit', payload);
    },
    onBubble(b) {
      if (b && b.turnId) this.$emit('select-turn', b.turnId);
    },
    bubbleClass(b) {
      if (b.side === 'system') return 'chat-bubble--system';
      // A peek is NOT a reply, so it must not look like one — its own muted, dashed treatment.
      if (b.side === 'state') return 'chat-bubble--state';
      return b.side === 'user' ? 'chat-bubble--user' : 'chat-bubble--bot';
    },
    /** A7 — the speaker is announced in text, not only by which side the bubble sits on. */
    srLabel(b) {
      if (b.side === 'user') return 'คุณ: ';
      if (b.side === 'system') return 'ระบบ: ';
      if (b.side === 'state') return 'สถานะฟอร์ม (ยังไม่ได้ส่งข้อความ): ';
      return 'บอท: ';
    },
    /** เวลาใต้บับเบิล = **เวลาไทย** (ค่ามาจาก `createdDate` ซึ่งเป็น UTC ไม่มี `Z` — ดู `utils/thaiTime.js`) */
    fmtTime(value) {
      return fmtThaiTime(value);
    },
  },
};
</script>

<style scoped>
/* 🔴 `height: 100%` is what makes `.chat-scroll` the ONLY scroller in this column.
   Without it the flex column was as tall as its content, `flex: 1 1 auto` on the
   bubble list had nothing to grow into, and the whole panel overflowed its column:
   the composer left the viewport and its `position: sticky` re-pinned it ON TOP of
   the last bubble. On xs the panel sits in an auto-height tab body, where 100%
   resolves to `auto` and the behaviour is unchanged. */
.tpg-chat {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

/* The alert / raw-wire switch / quick replies / composer state their own height —
   only the bubble list is elastic, and it stops shrinking at `min-height`. */
.tpg-chat > :not(.chat-scroll) {
  flex: 0 0 auto;
}

.chat-scroll {
  flex: 1 1 auto;
  min-height: 120px;
  overflow-y: auto;
  padding: 4px 2px;
}

.chat-col {
  max-width: 82%;
}

/* 🔴 A card carries a table-like breakdown; the 82% chat width squeezes the amount
   column until the numbers wrap, so a bubble that has cards takes the full row. */
.chat-col--card {
  max-width: 100%;
  width: 100%;
}

.tpg-cards {
  width: 100%;
}

.tpg-card-raw {
  margin: 4px 0 0;
  max-height: 180px;
  overflow: auto;
  font-size: 11px;
  line-height: 1.4;
  white-space: pre-wrap;
  word-break: break-all;
}

.tpg-raw-switch {
  flex: 0 0 auto;
}

/* 🔴 `position: relative` is LOAD-BEARING, not styling — do not drop it.
   It makes the bubble the containing block for its `.sr-only` label. That label is
   `position: absolute`, and an absolutely positioned box is clipped by an ancestor's
   `overflow` ONLY if that ancestor is between it and its containing block. Without a
   positioned bubble the nearest one is `.v-application__wrap`, so the label escaped
   `.chat-scroll`, `.tpg-col`, `.tpg-page` AND `.v-main` alike: its static position
   sits wherever its bubble scrolled to, so a 12-bubble conversation stretched the
   WHOLE app to 1338 px and gave every page a scrollbar that grew with the chat and
   ignored the window size. Measured: app overflow 406 px → 0 with this one line. */
.chat-bubble {
  position: relative;
  max-width: 100%;
  padding: 8px 12px;
  border-radius: 14px;
  font-size: 14px;
  line-height: 1.5;
  white-space: pre-wrap;
  word-break: break-word;
  cursor: pointer;
  transition: filter 0.12s ease;
}

.chat-bubble:hover {
  filter: brightness(0.96);
}

.chat-bubble--bot {
  background: #eceff1;
  color: #1a1a1a;
  border-top-left-radius: 4px;
}

.chat-bubble--user {
  background: #1e88e5;
  color: #fff;
  border-top-right-radius: 4px;
}

.chat-bubble--system {
  background: #ffebee;
  color: #b71c1c;
  border-top-left-radius: 4px;
  cursor: default;
}

/* The "Get Tax State" peek. Deliberately unlike a bot reply: dashed and grey, because it is the state the
   form is in RIGHT NOW, not something the flow said. `cursor: default` because `turnId` is 0 and `onBubble`
   does nothing — a pointer would advertise an interaction that is not there. */
.chat-bubble--state {
  background: #f5f5f5;
  color: #37474f;
  border: 1px dashed #b0bec5;
  border-top-left-radius: 4px;
  cursor: default;
}

.chat-bubble--pending {
  min-width: 180px;
  cursor: default;
}

.chat-time {
  font-size: 11px;
  padding: 0 4px;
  opacity: 0.75;
}

.tpg-composer {
  position: sticky;
  bottom: 0;
  background: rgb(var(--v-theme-surface));
  padding-top: 4px;
}

.sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}
</style>
